import React from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Input } from '../../../../../../../../CommonComponents/OPL/Input/Input';
import { Grid } from '../../../../../../../../CommonComponents/OPL/Grid/Grid';
import { Row } from '../../../../../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../../../../../CommonComponents/OPL/Grid/Column';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { ABBREVIATED_STATES_US } from '../../../../../../../../CommonConstants';
import { InputMask } from '../../../../../../../../CommonComponents/OPL/Input/InputMask';

interface Props {
  title: string;
  fieldName: string;
}

export const PersonalInfo: React.FC<Props> = ({ title, fieldName }) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const fieldErrors = errors[fieldName] as unknown as
    | Record<string, FieldError | undefined>
    | undefined;

  return (
    <Box as="section">
      <Text as="h3" fontSize="h6" fontWeight={500} mb="16">
        {title}
      </Text>

      <Input
        {...register(`${fieldName}.email`)}
        label="Email"
        leftIcon="far fa-envelope"
        type="text"
        placeholder="you@samplecapital.com"
        formVariant={fieldErrors?.email ? 'error' : undefined}
        helperText={fieldErrors?.email?.message}
        aria-label="Email Address"
        autoComplete="email"
        maxLength={256}
      />

      <Box my="16">
        <Input
          {...register(`${fieldName}.streetAddress`)}
          type="text"
          label="Address"
          placeholder="Street Name, #Apt, Floor"
          formVariant={fieldErrors?.streetAddress ? 'error' : undefined}
          helperText={fieldErrors?.streetAddress?.message}
          autoComplete="address-line1"
          maxLength={256}
        />

        <Grid gutter={8}>
          <Row mt="8">
            <Column width={6}>
              <Input
                {...register(`${fieldName}.city`)}
                type="text"
                placeholder="City"
                formVariant={fieldErrors?.city ? 'error' : undefined}
                helperText={fieldErrors?.city?.message}
                autoComplete="address-level2"
                aria-label="City"
                maxLength={256}
              />
            </Column>

            <Column width={3}>
              <Controller
                control={control}
                name={`${fieldName}.state`}
                render={({ field }) => (
                  <Select
                    isPortal
                    innerRef={field.ref}
                    value={ABBREVIATED_STATES_US.find(({ value }) => value === field.value)}
                    onChange={opt => {
                      if (opt) {
                        setValue(`${fieldName}.state`, opt.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }}
                    options={ABBREVIATED_STATES_US}
                    placeholder="State"
                    formVariant={fieldErrors?.state ? 'error' : undefined}
                    helperText={fieldErrors?.state?.message}
                    aria-label="State"
                  />
                )}
              />
            </Column>

            <Column width={3}>
              <InputMask
                {...register(`${fieldName}.postalCode`)}
                type="text"
                mask="99999"
                placeholder="Zip code"
                formVariant={fieldErrors?.postalCode ? 'error' : undefined}
                helperText={fieldErrors?.postalCode?.message}
                autoComplete="postal-code"
                aria-label="Zip/Postal Code"
              />
            </Column>
          </Row>
        </Grid>
      </Box>

      <InputMask
        {...register(`${fieldName}.phoneNumber`)}
        label="Primary Phone"
        type="tel"
        mask="999 999-9999"
        placeholder="(555) 867-5309"
        leftIcon="far fa-phone"
        formVariant={fieldErrors?.phoneNumber ? 'error' : undefined}
        helperText={fieldErrors?.phoneNumber?.message}
        aria-label="Phone Number"
        autoComplete="tel"
      />
    </Box>
  );
};
